// Turns a song's stored lyrics into the sectioned chord-over-lyric sheet the
// performance cockpit renders (ENG-359). Lyrics are free text entered by the
// band, so the parser accepts the conventions people actually type:
//
//   - blank lines separate sections (stanzas, when the sheet carries labels)
//   - a line that is only "[Verse 1]" or "Chorus:" labels the section it starts
//   - ChordPro inline chords: "[Em]When the night [C]has come"
//   - a chord-only line above a lyric line: the chords are attached to the
//     lyric by column, the way a plain-text chord sheet lines them up
//
// Output: [{ id, kind: 'lyric', label, lines: [[{ chord, lyric }, ...], ...] }].
// A segment with an empty `lyric` is a chord with nothing sung under it
// (chord-only lines); a segment with a null `chord` is plain lyric. An empty
// `lines` entry is a stanza break inside a labelled section.
//
// buildPerformanceSheet() layers the song's other stand fields on top: the
// separate chord chart, the performer notes and the tabs, so nothing the band
// saved for a song is missing from the stand.
//
// Labels the band typed ("[Verse 1]") are user content and stay as they are.
// Labels the parser makes up ("Part 1", "Chords", "Notes", "Tabs") are UI copy
// (ENG-386): they are resolved through the optional trailing `t` parameter
// (English without one, so non-React callers and the tests are unchanged) and
// the section also carries `labelKey` / `labelParams` so a component can
// re-resolve the label with its own `t` at render time.

import { englishT } from '../locales/index.js';

const SHARP_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const FLAT_NAMES = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B'];
// How a black key is usually spelled on a chart when nothing says otherwise
const DEFAULT_NAMES = ['C', 'C#', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'Ab', 'A', 'Bb', 'B'];
const PITCH_CLASS = {
  C: 0, 'C#': 1, Db: 1, D: 2, 'D#': 3, Eb: 3, E: 4, Fb: 4, F: 5, 'E#': 5, 'F#': 6, Gb: 6,
  G: 7, 'G#': 8, Ab: 8, A: 9, 'A#': 10, Bb: 10, B: 11, Cb: 11,
};

// Root + optional quality/extensions + optional slash bass, e.g. "F#m7",
// "G/B", "Csus4", "Am7b5", "E7#9", "Cmaj7", "G7sus4", "Dadd9".
const CHORD_TOKEN = /^([A-G])([#b]?)((?:maj|min|dim|aug|sus|add|m|M|\+|-|°|ø)?[0-9]*(?:[#b][0-9]+|(?:maj|min|dim|aug|sus|add|M)[0-9]*|\([^)]*\))*)(?:\/([A-G])([#b]?))?$/;

const SECTION_WORDS = 'intro|verse|pre-?chorus|chorus|bridge|outro|solo|interlude|hook|tag|refrain|instrumental|breakdown|coda|ending';
const BRACKET_LABEL = /^\[([^\]]+)\]\s*$/;
const WORD_LABEL = new RegExp(`^(?:${SECTION_WORDS})(?:\\s*[0-9]+)?\\s*:?\\s*$`, 'i');
const BRACKETED = /\[([^\]]+)\]/g;

export function isChordToken(token) {
  return CHORD_TOKEN.test(token);
}

export function isChordLine(line) {
  const tokens = line.trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return false;
  // A lone word like "A" or "Am" is ambiguous; treat single-token lines as
  // chords only when they carry chord-specific notation.
  if (tokens.length === 1 && !/[#b0-9\/]|m(?:aj|in)?$|dim|aug|sus/.test(tokens[0])) return false;
  return tokens.every(isChordToken);
}

function sectionLabelOf(line) {
  const trimmed = line.trim();
  const bracket = trimmed.match(BRACKET_LABEL);
  if (bracket) {
    const inner = bracket[1].trim();
    // "[Em]" alone is a chord, not a label
    if (isChordToken(inner)) return null;
    return inner;
  }
  if (WORD_LABEL.test(trimmed)) return trimmed.replace(/:\s*$/, '').trim();
  return null;
}

// Only brackets that hold a chord are chords: "[x2]" and "[spoken]" stay lyric
function hasInlineChord(line) {
  return [...line.matchAll(BRACKETED)].some((match) => isChordToken(match[1].trim()));
}

// "[Em]When the night [C]has come" -> segments split at each bracketed chord
function parseInlineChordLine(line) {
  const segments = [];
  let lastIndex = 0;
  let pendingChord = null;
  let match;
  BRACKETED.lastIndex = 0;
  while ((match = BRACKETED.exec(line)) !== null) {
    if (!isChordToken(match[1].trim())) continue;
    const text = line.slice(lastIndex, match.index);
    if (text.length > 0 || pendingChord !== null) {
      segments.push({ chord: pendingChord, lyric: text });
    }
    pendingChord = match[1].trim();
    lastIndex = BRACKETED.lastIndex;
  }
  const tail = line.slice(lastIndex);
  if (tail.length > 0 || pendingChord !== null) {
    segments.push({ chord: pendingChord, lyric: tail });
  }
  // "[A]  [F#m]  [D]" — nothing sung between the chords, so it is a chord-only line
  if (segments.every((seg) => seg.chord !== null && seg.lyric.trim() === '')) {
    return segments.map((seg) => ({ ...seg, lyric: '' }));
  }
  return segments;
}

// Chord line "Em        C" over lyric "When the night has come": each chord
// claims the lyric text from its column to the next chord's column.
function pairChordLineWithLyric(chordLine, lyricLine) {
  const chords = [];
  const re = /\S+/g;
  let match;
  while ((match = re.exec(chordLine)) !== null) {
    chords.push({ chord: match[0], column: match.index });
  }
  const segments = [];
  if (chords.length === 0 || chords[0].column > 0) {
    const lead = lyricLine.slice(0, chords[0]?.column ?? lyricLine.length);
    if (lead.length > 0) segments.push({ chord: null, lyric: lead });
  }
  chords.forEach(({ chord, column }, i) => {
    const end = i + 1 < chords.length ? chords[i + 1].column : lyricLine.length;
    segments.push({ chord, lyric: lyricLine.slice(column, Math.max(column, end)) });
  });
  return segments;
}

function chordOnlyLine(line) {
  return line.trim().split(/\s+/).filter(Boolean).map((chord) => ({ chord, lyric: '' }));
}

function plainLine(line) {
  return [{ chord: null, lyric: line.trimEnd() }];
}

// Generated-label keys (all in en-US/common.json under `lyricSheet`):
//   lyricSheet.part, lyricSheet.chords, lyricSheet.chordsNumbered,
//   lyricSheet.chordsSection, lyricSheet.notes, lyricSheet.tabs
const PART_LABEL_KEY = 'lyricSheet.part';

const labelled = (section, t, key, params = {}) => ({
  ...section,
  label: t(key, params),
  labelKey: key,
  labelParams: params,
});

export function parseLyricSheet(lyrics, t = englishT) {
  if (!lyrics || typeof lyrics !== 'string') return [];
  const rawLines = lyrics.replace(/\r\n?/g, '\n').split('\n');
  // With explicit labels, blank lines are stanza breaks inside the labelled
  // section ("[Chorus]" over two stanzas). Without any, they are the only
  // structure there is, so each block becomes its own section.
  const hasLabels = rawLines.some((line) => sectionLabelOf(line) !== null);

  const sections = [];
  let current = null;
  let pendingBreak = false;
  const startSection = (label) => {
    current = { id: `section-${sections.length + 1}`, kind: 'lyric', label, lines: [] };
    sections.push(current);
    pendingBreak = false;
  };

  for (let i = 0; i < rawLines.length; i += 1) {
    const line = rawLines[i];
    if (line.trim() === '') {
      if (current && current.lines.length > 0) {
        if (hasLabels && current.label) pendingBreak = true;
        else current = null;
      }
      continue;
    }

    const label = sectionLabelOf(line);
    if (label !== null) {
      if (current && current.lines.length === 0) {
        // Label directly after a label (or at the start): rename instead of nesting empties
        current.label = label;
      } else {
        startSection(label);
      }
      continue;
    }

    if (!current) startSection(null);
    if (pendingBreak) {
      current.lines.push([]);
      pendingBreak = false;
    }

    if (isChordLine(line)) {
      const next = rawLines[i + 1];
      if (next !== undefined && next.trim() !== '' && !isChordLine(next) && sectionLabelOf(next) === null) {
        current.lines.push(pairChordLineWithLyric(line, next.trimEnd()));
        i += 1;
      } else {
        current.lines.push(chordOnlyLine(line));
      }
      continue;
    }

    if (hasInlineChord(line)) {
      current.lines.push(parseInlineChordLine(line.trimEnd()));
      continue;
    }

    current.lines.push(plainLine(line));
  }

  // Unlabelled blocks are numbered so the section rail always has something to show
  let partNumber = 0;
  return sections
    .filter((section) => section.lines.length > 0)
    .map((section) => {
      if (section.label) return section;
      partNumber += 1;
      return labelled(section, t, PART_LABEL_KEY, { number: partNumber });
    });
}

const lineHasChord = (segments) => segments.some((seg) => seg.chord);
const lineIsChordOnly = (segments) => segments.length > 0 && segments.every((seg) => seg.lyric === '');

export function sheetHasChords(sections) {
  return sections.some((section) => section.kind === 'lyric' && section.lines.some(lineHasChord));
}

// Whether a parsed sheet has any words to sing, as opposed to chords, tabs or
// notes. Exported because "did the band already give us the words?" is the
// question that decides whether to spend a metered lyrics lookup (ENG-415),
// and the raw `lyrics` field does not answer it: a chords-over-lyrics chart
// in the `chords` field is the words, and buildPerformanceSheet puts it on
// the stand as such.
export function sheetHasSungText(sections) {
  return sections.some((section) =>
    section.kind === 'lyric' && section.lines.some((segments) => segments.some((seg) => seg.lyric.trim() !== '')),
  );
}

// Lyrics-only reading: drop the chord-only lines (and any section that was
// nothing but chords, such as a progression from the chord chart). Callers
// render the chord above each remaining segment or not; the section list here
// drives both the sheet and the section rail, so both stay in step.
export function withoutChordOnlyLines(sections) {
  return sections
    .map((section) => {
      if (section.kind !== 'lyric') return section;
      return { ...section, lines: section.lines.filter((segments) => !lineIsChordOnly(segments)) };
    })
    .filter((section) => section.kind !== 'lyric' || section.lines.some((segments) => segments.length > 0));
}

// The whole stand for a song. `lyrics` is the body; the separate `chords`
// field (the Add/Edit Song "Chord chart" textarea) is folded in rather than
// dropped: a full chart with words takes the place of plain lyrics that carry
// no chords of their own, anything else (a progression, per-section chords)
// goes ahead of the lyrics under a "Chords" label. `notes` open the stand and
// `tabs` close it, each as its own section so the rail can jump to them.
export function buildPerformanceSheet({ lyrics, chords, tabs, notes } = {}, t = englishT) {
  let body = parseLyricSheet(lyrics, t);
  const chart = parseLyricSheet(chords, t);

  if (chart.length > 0) {
    if (sheetHasChords(chart) && sheetHasSungText(chart) && !sheetHasChords(body)) {
      body = chart;
    } else {
      const chartSection = (section, index) => {
        if (chart.length === 1) return labelled(section, t, 'lyricSheet.chords');
        if (section.labelKey === PART_LABEL_KEY) return labelled(section, t, 'lyricSheet.chordsNumbered', { number: index + 1 });
        return labelled(section, t, 'lyricSheet.chordsSection', { section: section.label });
      };
      body = [...chart.map(chartSection), ...body];
    }
  }

  const sections = [];
  const noteText = typeof notes === 'string' ? notes.trim() : '';
  if (noteText) sections.push(labelled({ kind: 'text', text: noteText }, t, 'lyricSheet.notes'));
  sections.push(...body);
  const tabText = typeof tabs === 'string' ? tabs.replace(/\s+$/, '') : '';
  if (tabText.trim()) sections.push(labelled({ kind: 'tabs', text: tabText }, t, 'lyricSheet.tabs'));

  return sections.map((section, index) => ({ ...section, id: `section-${index + 1}` }));
}

// Spelling for the shifted note: 'flat' | 'sharp' | undefined (follow the source)
function spellRoot(root, accidental, semitones, accidentals) {
  const index = PITCH_CLASS[`${root}${accidental}`];
  if (index === undefined) return `${root}${accidental}`;
  const pitch = ((index + semitones) % 12 + 12) % 12;
  const preference = accidentals ?? (accidental === 'b' ? 'flat' : accidental === '#' ? 'sharp' : undefined);
  if (preference === 'flat') return FLAT_NAMES[pitch];
  if (preference === 'sharp') return SHARP_NAMES[pitch];
  return DEFAULT_NAMES[pitch];
}

// Shifts a chord symbol by `semitones`, keeping its quality and slash bass.
// Anything that does not parse as a chord is returned unchanged. Pass
// `accidentals` ('flat' | 'sharp') to spell every chord the way the song's
// transposed key is spelled; otherwise a flat chord stays flat, a sharp one
// stays sharp, and naturals land on the usual chart spelling (Bb, Eb, Ab, F#, C#).
export function transposeChord(chord, semitones, accidentals) {
  if (!chord || !semitones) return chord;
  const match = chord.trim().match(CHORD_TOKEN);
  if (!match) return chord;
  const [, root, accidental, quality = '', bassRoot, bassAccidental = ''] = match;
  const shifted = spellRoot(root, accidental, semitones, accidentals) + quality;
  return bassRoot ? `${shifted}/${spellRoot(bassRoot, bassAccidental, semitones, accidentals)}` : shifted;
}

// Song keys are free text ("Em", "G", "Bb major"); only the leading note is
// shifted so "G major" transposed up two becomes "A major".
export function transposeKey(key, semitones) {
  if (!key || !semitones) return key;
  const match = String(key).trim().match(/^([A-G])([#b]?)(.*)$/);
  if (!match) return key;
  const [, root, accidental, rest] = match;
  return spellRoot(root, accidental, semitones) + rest;
}

// Which spelling a (possibly transposed) key implies for the chords under it
export function accidentalsForKey(key) {
  const match = String(key ?? '').trim().match(/^[A-G]([#b])/);
  if (!match) return undefined;
  return match[1] === 'b' ? 'flat' : 'sharp';
}
